import { Store } from "@reduxjs/toolkit";
import { RootState } from "redux/rootReducer";
import {
  authInitialized,
  sessionExpired,
  sessionLoaded,
  sessionRefreshed,
} from "redux/auth/authSlice";
import {
  AUTH_STORAGE_KEY,
  AuthSession,
  clearSession,
  decodeJwtPayload,
  loadSession,
  saveSession,
} from "./session";
import { createTokenProvider, TokenProvider } from "./tokenProvider";

type AppStore = Store<RootState>;

/** Token provider backed by the Redux auth state. */
export function createStoreTokenProvider(store: AppStore): TokenProvider {
  return createTokenProvider({
    getSession: () => store.getState().auth.session,
    onRefreshed: (session) => store.dispatch(sessionRefreshed(session)),
    onExpired: () => store.dispatch(sessionExpired()),
  });
}

/** Identifies the logged-in user, so a token refresh isn't mistaken for a new user. */
function userKey(session: AuthSession | null): string | null {
  if (!session) return null;
  return (decodeJwtPayload(session.idToken).sub as string) ?? session.email;
}

interface StartAuthOptions {
  store: AppStore;
  tokenProvider: TokenProvider;
  /** Called when the user logs in, logs out or the session expires (not on refresh). */
  onUserChanged: () => void;
}

/**
 * Starts the auth lifecycle:
 *  - loads the stored session and refreshes it if stale (or expires it),
 *  - persists every session change to storage,
 *  - notifies when the user changes (to reset the GraphQL cache),
 *  - keeps other tabs in sync.
 * Returns a cleanup function.
 */
export function startAuth({
  store,
  tokenProvider,
  onUserChanged,
}: StartAuthOptions): () => void {
  store.dispatch(sessionLoaded(loadSession()));
  let previous = store.getState().auth.session;

  const unsubscribe = store.subscribe(() => {
    const current = store.getState().auth.session;
    if (current === previous) return;

    if (current) saveSession(current);
    else clearSession();

    const userChanged = userKey(previous) !== userKey(current);
    previous = current;
    if (userChanged) onUserChanged();
  });

  // Refresh a stale stored session up front so the UI shows the right state.
  // A network failure keeps the stored session; it is retried on the next request.
  tokenProvider
    .getValidIdToken()
    .catch(() => undefined)
    .finally(() => store.dispatch(authInitialized()));

  const onStorage = (event: StorageEvent) => {
    if (event.key === AUTH_STORAGE_KEY) store.dispatch(sessionLoaded(loadSession()));
  };
  window.addEventListener("storage", onStorage);

  return () => {
    unsubscribe();
    window.removeEventListener("storage", onStorage);
  };
}
